import { useCallback, useMemo } from 'react';
import { useAtom } from 'jotai';
import {
  isVoiceRecordingAtom,
  voiceModeAtom
} from '../atoms/v3/chatInputAtoms';
import { useToast } from '@chakra-ui/react';

/**
 * Chat 页面快捷键业务逻辑 Hook
 * 提供所有 Chat 页面快捷键的具体实现函数
 */
export const useChatShortcutActions = () => {
  const [isVoiceRecording] = useAtom(isVoiceRecordingAtom);
  const [voiceMode, setVoiceMode] = useAtom(voiceModeAtom);
  const addToast = useToast();

  // 获取录音相关的函数
  const getVoiceRecordingFunctions = useCallback(() => {
    // 查找录音按钮和相关元素
    const voiceButton = document.querySelector('[data-voice-button]') as HTMLElement;
    const chatInput = document.querySelector('[data-chat-input]') as HTMLInputElement | HTMLTextAreaElement;
    
    return {
      voiceButton,
      chatInput
    };
  }, []);

  // 开始/结束录音
  const toggleVoiceRecording = useCallback(() => {
    console.log('🔥 Chat: Toggle voice recording - SHORTCUT TRIGGERED!');
    console.log('🔍 Chat: Current state - voiceMode:', voiceMode, 'isRecording:', isVoiceRecording);

    const { voiceButton } = getVoiceRecordingFunctions();

    if (voiceButton) {
      // 通过点击现有UI按钮来触发（保持UI一致性）
      console.log('🔥 Chat: Found voice button, clicking it');
      voiceButton.click();
    } else {
      console.log('🔥 Chat: Voice button not found, trying alternative UI elements');

      // 如果正在录音，寻找停止录音的UI元素
      if (isVoiceRecording) {
        // 查找"转文字编辑"按钮（这会停止录音并切换到文本模式）
        const editButton = document.querySelector('button:contains("转文字编辑"), button:contains("📝")') as HTMLElement;
        if (editButton) {
          console.log('🔥 Chat: Clicking edit button to stop recording');
          editButton.click();
          return;
        }
      }

      // 如果没有在语音模式，先切换到语音模式
      if (!voiceMode) {
        console.log('🔥 Chat: Switching to voice mode first');
        setVoiceMode(true);

        // 延迟一下确保 UI 更新完成，然后再次尝试点击录音按钮
        setTimeout(() => {
          const voiceButtonAfterSwitch = document.querySelector('[data-voice-button]') as HTMLElement;
          if (voiceButtonAfterSwitch) {
            console.log('🔥 Chat: Clicking voice button after mode switch');
            voiceButtonAfterSwitch.click();
          }
        }, 100);
      }
    }
  }, [isVoiceRecording, voiceMode, setVoiceMode, getVoiceRecordingFunctions]);

  // 取消录音
  const cancelRecording = useCallback(() => {
    console.log('Chat: Cancel recording');
    
    if (isVoiceRecording) {
      // 发送取消录音事件
      window.dispatchEvent(new CustomEvent('cancelVoiceRecording'));
      
      // 退出语音模式，回到文本模式
      setVoiceMode(false);
      
      // 显示取消提示
      addToast({
        title: '录音已取消',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
    }
  }, [isVoiceRecording, setVoiceMode, addToast]);

  // 注意：sendMessage 方法已移除，因为回车发送功能现在由 TextInput 组件直接处理

  // 聚焦到聊天输入框
  const focusChatInput = useCallback(() => {
    console.log('Chat: Focus chat input');
    
    const { chatInput } = getVoiceRecordingFunctions();
    
    if (chatInput) {
      chatInput.focus();
    } else {
      // 尝试多种选择器
      const selectors = [
        '[data-chat-input]',
        'textarea[placeholder*="帮我规划"]',
        'textarea[placeholder*="日程"]',
        'textarea[placeholder*="消息"]',
        'input[placeholder*="消息"]',
        'textarea[placeholder*="Message"]',
        'input[placeholder*="Message"]',
        '.chat-input textarea',
        '.chat-input input',
        'textarea[rows]',
        'input[type="text"]'
      ];

      for (const selector of selectors) {
        const element = document.querySelector(selector) as HTMLInputElement | HTMLTextAreaElement;
        if (element && element.offsetParent !== null) {
          element.focus();
          console.log(`Chat input focused using selector: ${selector}`);
          return;
        }
      }
      
      console.warn('Failed to focus chat input');
    }
  }, [getVoiceRecordingFunctions]);

  // 新建对话（暂时保留，但可能需要外部实现）
  const newConversation = useCallback(() => {
    console.log('Chat: New conversation');
    // 这个功能可能需要导航到新对话页面
    // 暂时使用自定义事件
    window.dispatchEvent(new CustomEvent('newConversation'));
  }, []);

  // 对话历史（暂时保留，但可能需要外部实现）
  const conversationHistory = useCallback(() => {
    console.log('Chat: Conversation history');
    // 这个功能可能需要打开对话历史面板
    // 暂时使用自定义事件
    window.dispatchEvent(new CustomEvent('conversationHistory'));
  }, []);

  // 注意：isInputFocused 方法已移除，因为输入框焦点检测现在由快捷键系统统一处理

  // 检查是否有文件上传覆盖层
  const isFileUploadOverlayOpen = useCallback(() => {
    const overlay = document.querySelector('[data-file-upload-overlay]');
    return !!overlay;
  }, []);

  // 关闭文件上传覆盖层
  const closeFileUploadOverlay = useCallback(() => {
    console.log('Chat: Close file upload overlay');
    
    if (isFileUploadOverlayOpen()) {
      // 查找关闭按钮
      const closeButton = document.querySelector('[data-file-upload-close]') as HTMLElement;
      if (closeButton) {
        closeButton.click();
      } else {
        // 回退到 ESC 键事件
        const escEvent = new KeyboardEvent('keydown', { key: 'Escape' });
        document.dispatchEvent(escEvent);
      }
    }
  }, [isFileUploadOverlayOpen]);

  return useMemo(() => ({
    // 录音相关
    toggleVoiceRecording,
    cancelRecording,

    // 消息相关
    focusChatInput,

    // 对话管理
    newConversation,
    conversationHistory,

    // 文件上传
    closeFileUploadOverlay,

    // 状态检查
    isFileUploadOverlayOpen,
    isVoiceRecording: () => isVoiceRecording
  }), [
    toggleVoiceRecording,
    cancelRecording,
    focusChatInput,
    newConversation,
    conversationHistory,
    closeFileUploadOverlay,
    isFileUploadOverlayOpen,
    isVoiceRecording
  ]);
};
